import { IPage } from 'models/common';
import {
	IShopSubscriptionPlansState,
	IShopSubscriptionPlanPost,
	IShopSubscriptionPlanPut,
} from 'models/subscriptionplans';
import { Dispatch } from 'react-redux';
import { IStore } from 'redux/IStore';

/** Action Types */
export const REQUEST_START: string = 'shopsubscriptionplans/REQUEST_START';
export const REQUEST_FAILURE: string = 'shopsubscriptionplans/REQUEST_FAILURE';
export const RESET_DATA: string = 'shopsubscriptionplans/RESET_DATA';
export const SET_PLANS: string = 'shopsubscriptionplans/SET_PLANS';
export const SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS: string =
	'shopsubscriptionplans/SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS';
export const SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS_ITEMS_PAGE: string =
	'shopsubscriptionplans/SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS_ITEMS_PAGE';

interface IShopSubscriptionPlansAction {
	type: string;
	payload?: {
		message?: any;
		shopPlans?: IShopSubscriptionPlansState['shopPlans'];
		shopId?: string;
		shopsWithActiveSubscriptionPlan?: IShopSubscriptionPlansState['shopsWithActiveSubscriptionPlan'];
	};
}

/** Initial State */
const getinitialState = (): IShopSubscriptionPlansState => ({
	shopPlans: null,
	shopId: null,
	shopsWithActiveSubscriptionPlan: {
		itemsPerPage: 10,
		currentPage: 0,
		totalItems: 0,
		totalPages: 0,
		items: [],
	},
});

/** Reducer */
export function shopSubscriptionPlansReducer(
	state = getinitialState(),
	action: IShopSubscriptionPlansAction,
): IShopSubscriptionPlansState {
	switch (action.type) {
		case REQUEST_START:
			return {
				...state,
				isFetching: true,
				error: false,
				message: undefined,
			};

		case SET_PLANS:
			return {
				...state,
				isFetching: false,
				error: false,
				message: undefined,
				shopPlans: action.payload.shopPlans,
			};

		case REQUEST_FAILURE:
			return {
				...state,
				isFetching: false,
				error: true,
				message: action.payload.message,
			};

		case SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS:
			return {
				...state,
				isFetching: false,
				error: false,
				message: undefined,
				shopsWithActiveSubscriptionPlan: Object.assign(
					{},
					state.shopsWithActiveSubscriptionPlan,
					action.payload.shopsWithActiveSubscriptionPlan,
				),
			};
		case SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS_ITEMS_PAGE:
			return {
				...state,
				shopsWithActiveSubscriptionPlan: Object.assign(
					{},
					state.shopsWithActiveSubscriptionPlan,
					action.payload.shopsWithActiveSubscriptionPlan,
				),
			};

		case RESET_DATA:
			return getinitialState();

		default:
			return state;
	}
}

export function requestStart(): IShopSubscriptionPlansAction {
	return {
		type: REQUEST_START,
	};
}

export function resetData(): IShopSubscriptionPlansAction {
	return {
		type: RESET_DATA,
	};
}

export function setShopPlans(
	shopPlans: IShopSubscriptionPlansState['shopPlans'],
	shopId: string,
): IShopSubscriptionPlansAction {
	return {
		type: SET_PLANS,
		payload: {
			shopPlans,
			shopId,
		},
	};
}

export function setShopsWithActiveSubscriptionPlan(
	shopsWithActiveSubscriptionPlan: IShopSubscriptionPlansState['shopsWithActiveSubscriptionPlan'],
): IShopSubscriptionPlansAction {
	return {
		type: SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS,
		payload: {
			shopsWithActiveSubscriptionPlan,
		},
	};
}

/**
 * Does not change isFetching, isLoading ...
 */
export function setShopsWithActiveSubscriptionPlanItemsPage(data: IPage<null>): IShopSubscriptionPlansAction {
	return {
		type: SET_SHOPS_WITH_ACTIVE_SUBSCRIPTION_PLANS_ITEMS_PAGE,
		payload: {
			shopsWithActiveSubscriptionPlan: data,
		},
	};
}

export function requestFailure(message: any): IShopSubscriptionPlansAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

export function getShopSubscriptionPlans(shopId: string) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + `api/SubscriptionPlan/shop/${shopId}`,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((json) => {
						dispatch(setShopPlans(json, shopId));
						return { error: false };
					});
				} else {
					res
						.json()
						.then((json) => {
							dispatch(requestFailure(json.error || res.statusText));
						})
						.catch(() => {
							dispatch(requestFailure(res.statusText));
						});
					return { error: true };
				}
			})
			.catch((e) => {
				console.error(e);
				return { error: true };
			});
	};
}

export function getShopSmsCount(shopId: string, dateValues: any) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		const params = new URLSearchParams({
			dateFrom: String(dateValues.dateFrom),
			dateTo: String(dateValues.dateTo),
		});

		return fetch(
			getState().fetchDefault.baseAPI + `api/Shop/${shopId}/Sms?${params.toString()}`,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((json) => {
						return json;
					});
				} else {
					res
						.json()
						.then((json) => {
							dispatch(requestFailure(json.error || res.statusText));
						})
						.catch(() => {
							dispatch(requestFailure(res.statusText));
						});
					return { error: true };
				}
			})
			.catch((e) => {
				console.error(e);
				return { error: true };
			});
	};
}

export function getShopsWithActiveSubscriptionPlans(currentPage?: number, itemsPerPage?: number) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		dispatch(requestStart());

		const params = new URLSearchParams({
			currentPage: String(
				currentPage || getState().shopSubscriptionPlansReducer.shopsWithActiveSubscriptionPlan.currentPage + 1,
			),
			itemsPerPage: String(
				itemsPerPage || getState().shopSubscriptionPlansReducer.shopsWithActiveSubscriptionPlan.itemsPerPage,
			),
		});

		return fetch(
			getState().fetchDefault.baseAPI + `api/SubscriptionPlan/Shop?${params.toString()}`,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((json) => {
						let items = json.items;
						if (!currentPage) {
							const existingItems = getState().shopSubscriptionPlansReducer.shopsWithActiveSubscriptionPlan.items;
							items = [...existingItems, ...json.items];
						}
						dispatch(setShopsWithActiveSubscriptionPlan({ ...json, items }));
						return { error: false };
					});
				} else {
					res
						.json()
						.then((json) => {
							dispatch(requestFailure(json.error || res.statusText));
						})
						.catch(() => {
							dispatch(requestFailure(res.statusText));
						});
					return { error: true };
				}
			})
			.catch((e) => {
				console.error(e);
				return { error: true };
			});
	};
}

export function postShopSubscriptionPlan(plan: IShopSubscriptionPlanPost) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + `api/SubscriptionPlan/Shop/${plan.shopId}`,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(plan),
			}),
		).then((res) => {
			if (res.ok) {
				return dispatch(getShopSubscriptionPlans(plan.shopId));
			} else {
				res
					.json()
					.then((json) => {
						dispatch(requestFailure(JSON.stringify(json?.error) || res.statusText));
					})
					.catch(() => {
						dispatch(requestFailure(res.statusText));
					});
				return { error: true };
			}
		});
	};
}

export function putShopSubscriptionPlan(plan: IShopSubscriptionPlanPut) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + `api/SubscriptionPlan/Shop/${plan?.shopId}`,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(plan),
			}),
		).then((res) => {
			if (res.ok) {
				return dispatch(getShopSubscriptionPlans(plan.shopId));
			} else {
				res
					.json()
					.then((json) => {
						dispatch(requestFailure(JSON.stringify(json?.error) || res.statusText));
					})
					.catch(() => {
						dispatch(requestFailure(res.statusText));
					});
				return { error: true };
			}
		});
	};
}
